/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.SearchQueryService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods to manage search queries and filters.
 */
SearchQueryService.$inject = [
	'$q',
	'mx.shell.EventService',
	'mx.shell.SearchProviderService',
	'mx.shell.NotificationService',
	'mx.SolutionBuilderAgent.mxColumnDefinitionService',
	'mx.SolutionBuilderAgent.EntityService',
	'mx.SolutionBuilderAgent.DataQueryService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.CacheService',
	'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
	'mx.SolutionBuilderAgent.SearchQueryCondition',
	'mx.SolutionBuilderAgent.SearchQueryGroup',
	'mx.SolutionBuilderAgent.SearchQuery',
	'$stateParams',
];

export default function SearchQueryService(
	$q,
	eventService,
	searchService,
	notificationService,
	columnDefinitionService,
	entityService,
	dataQueryService,
	internationalization,
	cacheService,
	filterOperatorsFactory,
	SearchQueryCondition,
	SearchQueryGroup,
	SearchQuery,
	$stateParams
) {
	const queriesCache = {};

	eventService.subscribeGlobal(mx.shell.EventCodes.CurrentNavigationItemChanged, (e, { navigationItem }) => onNavigationItemChanged(navigationItem));

	return {
		getQueries: getQueries,
		getFilters: getFilters,
		openFilterDialog: openFilterDialog,
		createConditionByColumn: createConditionByColumn,
		createExpressionCondition: createExpressionCondition,
		initQueryByContext: initQueryByContext,
		initQueryByDataQuery: initQueryByDataQuery,
		createQuery: createQuery,
		createQueryGroup: createQueryGroup,
		createQueryCondition: createQueryCondition,
		saveSearchQuery: saveSearchQuery,
		addSearchQuery: addSearchQuery,
		removeSearchQuery: removeSearchQuery,
		getConditionString: getConditionString,
		editFilter: editFilter,
		addChildFilter: addChildFilter,
		deleteFilter: deleteFilter,
		getContextByDataQueryId: getContextByDataQueryId,
		validateGroup: validateGroup,
	};

	async function onNavigationItemChanged(navigationItem) {
		if (navigationItem?.searchContext) {
			const queries = ((await getQueries(navigationItem.searchContext)) || []).filter((query) => !query.showInNavigation);
			queries.forEach((query) => (query.__active = false));

			if ($stateParams.extraFilter) {
				const query = queries.find((query) => query.id === $stateParams.extraFilter);
				if (query) {
					query.__active = true;
				}
			}
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.SearchQueryService#editFilter
	 *
	 * @description
	 * Opens a dialog to edit the specified filter.
	 *
	 * @param {object} filter - a filter to edit.
	 */
	function editFilter(filter) {
		const context = _getContextByFilter(filter);
		getQueries(context, false).then(function (queries) {
			if (!Array.isArray(queries)) {
				return;
			}

			let query = null;
			let parent = null;

			queries.forEach((item) => {
				const id = item.id;

				if (id === filter.id) {
					query = item;
				}

				if (id === filter.parent) {
					parent = item;
				}
			});

			openFilterDialog(query, context, parent);
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.SearchQueryService#deleteFilter
	 *
	 * @description
	 * Deletes the filter.
	 *
	 * @param {object} filter - a filter to delete
	 */
	function deleteFilter(filter) {
		return notificationService
			.confirm(
				'',
				internationalization.get('solutionBuilderAgent.components.mx-search-query-builder.confirmDelete'),
				internationalization.get('shell.common.yes'),
				internationalization.get('shell.common.no')
			)
			.then(() => {
				const context = _getContextByFilter(filter);
				return removeSearchQuery(filter.id, context).then(() => {
					notificationService.success(internationalization.get('solutionBuilderAgent.components.mx-search-query-builder.filterDeleted'));
					return true;
				});
			});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.SearchQueryService#addChildFilter
	 *
	 * @description
	 * Opens a dialog to create a child filter.
	 *
	 * @param {object} filter - a filter to create child for
	 */
	function addChildFilter(filter) {
		const context = _getContextByFilter(filter);
		getQueries(context, false).then((queries) => {
			const parent = (queries || []).find((item) => item.id === filter.id) || null;
			openFilterDialog(null, context, parent, true);
		});
	}

	function _getContextByFilter(filter) {
		return getContextByDataQueryId(filter.dataQuery, filter.widgetId);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.SearchQueryService#getContextByDataQueryId
	 *
	 * @description
	 * Gets a search context for DataQuery by its id
	 *
	 * @param {guid} dataQueryId - id of a DataQuery to get context for
	 * @param {guid} widgetId - id of a widget to get context for
	 */
	function getContextByDataQueryId(dataQueryId, widgetId) {
		const contexts = searchService.getContexts();
		const filters = contexts.filter((item) => item.datasource === dataQueryId);

		if (filters.length > 1 && Boolean(widgetId)) {
			const widgetFilters = filters.filter((item) => item.widgetId === widgetId);
			if (widgetFilters.length > 0) {
				return widgetFilters[0];
			}
		}

		return filters[0];
	}

	function getConditionString(condition) {
		const values = condition.value;

		if (condition.type === 'Expression') {
			return `"<i><b>${values[1]}</b></i>"`;
		}

		const operator = filterOperatorsFactory.getOperatorByValue(condition.operator);
		const valueString = condition.valueString;

		if (!values || (!values[0] && !values[1] && condition.type !== 'BoolType')) {
			return '';
		}

		let value = values[0] || '';
		let secondValue = values[1] || '';

		if (condition.type === 'DateType') {
			value = internationalization.formatDate(value, 'longDate');
			secondValue = internationalization.formatDate(secondValue, 'longDate');
		} else if (condition.type === 'BoolType') {
			value = value ? 'True' : 'False';
		}

		if (operator) {
			let res = '';

			switch (operator.value) {
				case filterOperatorsFactory.operators.In.value:
					if (condition.type === 'IntType' && condition.__enumeration) {
						res += ` <b>${_getLocalization('capIs')}</b> `;
						res += enumerationValuesString(values, condition.__enumeration);
					} else if (valueString) {
						res += ' <b>' + (values.length > 1 ? _getLocalization('are') : _getLocalization('is')) + ' </b>' + valueString;
					} else {
						res += ` <b>${_getLocalization('inSelectObjects')} (${values.length})</b>`;
					}
					break;
				case filterOperatorsFactory.operators.NotIn.value:
					if (condition.type === 'IntType' && condition.__enumeration) {
						res += ' <b>' + _getLocalization('capIsn') + '</b> ';
						res += enumerationValuesString(values, condition.__enumeration);
					} else if (valueString) {
						res += ` <b> ${values.length > 1 ? _getLocalization('arenot') : _getLocalization('isnot')} </b>${valueString}`;
					} else {
						res += ` <b> ${_getLocalization('inNotSelectObjects')} (${values.length})</b>`;
					}
					break;

				default:
					res += `<b>${operator.name}</b> `;

					if (valueString) {
						res += valueString;
					} else {
						res += `"<u title="${value}">${value}</u>" `;
						if (secondValue) {
							res += `${_getLocalization('and')} "<b>${secondValue}</b>"`;
						}
					}

					break;
			}
			return res;
		}
		return '';
	}

	function enumerationValuesString(values, enumeration) {
		let res = '';

		for (let i = 0; i < values.length; i++) {
			const enumValue = _.find(enumeration, ['Value', Number(values[i])]);

			if (enumValue) {
				if (i !== 0 && i !== values.length - 1) {
					res += ',';
				} else if (i !== 0 && i === values.length - 1) {
					res += ' or';
				}
				res += ` "<u>${enumValue.DisplayString}</u>"`;
			}
		}
		return res;
	}

	function _getLocalization(string) {
		return internationalization.get(`solutionBuilderAgent.components.mx-search-query-condition.${string}`);
	}

	function getFilters(dataQueryId, listWidgetId) {
		return dataQueryService.getFilters(dataQueryId, listWidgetId).then((filters) => {
			filters.forEach((filter) => {
				filter.Widget = listWidgetId;
				getParentsChain(filter, filters);
			});

			return filters;
		});
	}

	function getParentsChain(currentFilter, filters) {
		if (currentFilter.parentsChain) {
			return currentFilter.parentsChain;
		}

		currentFilter.ParentsChain = [];
		if (currentFilter.Parent && currentFilter.ConsiderParentFilters) {
			filters.some(function (filter) {
				if (currentFilter.Parent === filter.Id) {
					currentFilter.ParentsChain = [filter].concat(getParentsChain(filter, filters));
					return true;
				}
			});
		}
		return currentFilter.ParentsChain;
	}

	function getQueries(context, fromCache) {
		const defer = $q.defer();

		if (fromCache === undefined) {
			fromCache = true;
		}

		const key = context && context.name ? context.name : null;

		if (fromCache && key && queriesCache[key] && queriesCache[key].length) {
			defer.resolve(queriesCache[key]);
		} else {
			$q.all([getFilters(context.datasource, context.widgetId), getDataQuery(context)]).then((data) => {
				const qData = data[0];
				const dataQuery = data[1];

				if (Array.isArray(qData)) {
					let queries = qData.map((item) => createQuery(item));

					queries = queries.sort((a, b) => a.position - b.position);

					const queryPromises = queries.map((query) => initQueryByDataQuery(query, dataQuery));

					$q.all(queryPromises).then(function () {
						if (key) {
							queriesCache[key] = queries;
						}
						defer.resolve(queries);
					});
				}
			});
		}

		return defer.promise;
	}

	async function openFilterDialog(query, context, parent, showInNavigation) {
		if (!context) {
			throw new Error('No context provided');
		}

		const title = internationalization.get(`solutionBuilderAgent.components.mx-search-query-builder.${query ? 'editQuery' : 'createQuery'}`);

		if (!query?.group) {
			query = createQuery(query);
		}

		if (!parent && query.parent) {
			parent = ((await getQueries(context, false)) || []).find((item) => item.id === query.parent);
		}

		searchService.openSearchQueryBuilder({
			title: title,
			query: query,
			context: context,
			parent: parent,
			showInNavigation: showInNavigation || (parent && parent.showInNavigation),
		});
	}

	function createQuery(queryOptions) {
		return new SearchQuery(queryOptions);
	}

	function createQueryGroup(groupOptions) {
		return new SearchQueryGroup(groupOptions);
	}

	function createQueryCondition(conditionOptions) {
		return new SearchQueryCondition(conditionOptions);
	}

	function getDataQuery(context) {
		if (context) {
			const widgetId = context.widgetId || (context.parameters && context.parameters.contentWidgetId);
			return columnDefinitionService.getGridDefinitionById(context.datasource, widgetId);
		} else {
			return $q.when({ Columns: [] });
		}
	}

	function initQueryByContext(query, context) {
		return getDataQuery(context).then((dataQuery) => {
			context.className = dataQuery.SchemaClassName;
			return initQueryByDataQuery(query, dataQuery);
		});
	}

	function initQueryByDataQuery(query, dataQuery) {
		return initGroup(query.group, dataQuery);
	}

	function initGroup(group, dataQuery) {
		const defer = $q.defer();
		const res = [];

		group.conditions.forEach((c) => {
			if (!c.__property) {
				let dataQueryColumn = null;

				dataQuery.Columns.some(function (column) {
					if (column.Name === c.property) {
						dataQueryColumn = column;
						return true;
					}
				});
				res.push(initCondition(c, dataQueryColumn, dataQuery));
			}
		});

		group.groups.forEach((g) => {
			res.push(initGroup(g, dataQuery));
		});

		$q.all(res).then(() => {
			defer.resolve(group);
		});

		return defer.promise;
	}

	function initCondition(condition, dataQueryColumn, dataQuery) {
		condition.__property = dataQueryColumn;
		condition.type = dataQueryColumn ? dataQueryColumn.AttributeSearchValueType : condition.type;

		if (condition.type === 'IntType' && dataQueryColumn && dataQueryColumn.ReferenceTo) {
			if (dataQuery.Service) {
				return getEnumerationFromService(condition, dataQueryColumn, dataQuery);
			} else {
				return getEnumeration(condition, dataQueryColumn);
			}
		}
		return $q.when(condition);
	}

	function getEnumerationFromService(condition, property, dataQuery) {
		const listOperation = dataQuery.ServiceInfo.Operations.filter((op) => op.Id === dataQuery.Operation)[0];

		const enumInfo = listOperation.ResponseContract.UsedTypes[property.ReferenceTo];
		if (!enumInfo) {
			condition.__namespace = undefined;
			return getEnumeration(condition, property);
		}

		const _enum = enumInfo.Enum;

		condition.__namespace = enumInfo.NameSpace;
		if (enumInfo.Flags) {
			condition.__flags = enumInfo.Flags;
		}

		const enumeration = [];

		Object.keys(_enum).forEach((paramName) => {
			const value = _enum[paramName];

			enumeration.push({
				DisplayString: value.Title,
				Value: value.Value,
			});
		});

		condition.__enumeration = enumeration;
		return condition;
	}

	function getEnumeration(condition, property) {
		return entityService.getEnumerationValues(property.ReferenceTo, property.ReferenceFilter).then((enumeration) => {
			condition.__enumeration = enumeration;
			return condition;
		});
	}

	function createExpressionCondition() {
		const condition = createQueryCondition({
			Property: '__expression',
			Type: 'Expression',
		});

		return initCondition(condition);
	}

	function createConditionByColumn(columnName, dataQuery) {
		const column = dataQuery.Columns.find((column) => column.Name === columnName);
		if (!column) {
			return $q.when(null);
		}
		const condition = createQueryCondition({
			Property: column.Name,
			Type: column.AttributeSearchValueType,
			Operator: column.AttributeSearchValueType === mx.shell.ObjectPropertyType.String.name ? filterOperatorsFactory.operators.Contains.value : undefined,
		});

		return initCondition(condition, column, dataQuery);
	}

	function addSearchQuery(query, context) {
		query.dataQuery = context.datasource;
		query.datasetView = context.widgetId;
		return dataQueryService.createFilter(query).then(reloadNavigation(context));
	}

	function saveSearchQuery(query, context) {
		return dataQueryService.updateFilter(query).then(reloadNavigation(context));
	}

	function removeSearchQuery(queryId, context) {
		return dataQueryService.removeFilter(queryId).then(reloadNavigation(context));
	}

	function reloadNavigation(context) {
		return function (id) {
			return cacheService.resetServerCache({ navigation: true }).then(function () {
				eventService.dispatchEvent(mx.shell.EventCodes.NavigationReload);
				if (queriesCache && queriesCache[context.name]) {
					delete queriesCache[context.name];
				}
				return id;
			});
		};
	}

	function validateConditions(conditions) {
		return conditions.every((condition) => condition.__valid);
	}

	function validateGroup(group) {
		if (!group || typeof group !== 'object') {
			return false;
		}

		const hasConditions = Array.isArray(group.conditions) && group.conditions.length > 0;
		const hasGroups = Array.isArray(group.groups) && group.groups.length > 0;
		if (!hasConditions && !hasGroups) {
			return false;
		}

		return validateConditions(group.conditions || []) && (group.groups || []).every((group) => validateGroup(group));
	}
}
